<template>
  <div :style="containerStyle">
    <router-view
      :key="$route.fullPath"
      :tenantTeamPermissions="tenantTeamPermissions"
      @updated-tenant-team-permissions="fetchTenantTeamPermissions"
    />
  </div>
</template>

<script>
  import HttpStatus from 'http-status-codes'

  import ApiTenantTeamPermission from '@js/api/tenant-team-permission'
  import ApiTenantTeamSkill from '@js/api/tenant-team-skill'
  import ApiTenantTeamTenantUser from '@js/api/tenant-team-tenant-user'
  import ApiTenantTeamTenantUserSkill from '@js/api/tenant-team-tenant-user-skill'
  import ApiTenantTeamView from '@js/api/tenant-team-view'
  import { mapGetters, mapState } from 'vuex'
  import { Layout } from '@js/styles'

  export default {
    name: 'TeamupTeamRootView',
    data: function () {
      return {
        containerStyle: {
          maxWidth: Layout.container.primary.maxWidth,
          minHeight: '600px',
          width: '100%',
        },
      };
    },
    created: async function () {
      const tenantTeamCode = this.$route.params.code;
      if (tenantTeamCode == null || tenantTeamCode.length === 0) {
        this.$router.push({ name: 'teamup-select' });
        return;
      }

      const accessNewTenantTeam = tenantTeamCode !== this.tenantTeam.code;
      if (accessNewTenantTeam) {
        await this.$store.commit('teamup/initialize');
      }

      await this.fetchTenantTeamPermissions(tenantTeamCode);
      await this.fetchTenantTeamView(tenantTeamCode);

      if (accessNewTenantTeam || !this.loaded('tenantTeamTenantUsers')) {
        await this.fetchTenantTeamTenantUsers();
      }
      if (accessNewTenantTeam || !this.loaded('tenantTeamSkills')) {
        await this.fetchTenantTeamSkills();
      }
      if (accessNewTenantTeam || !this.loaded('tenantTeamTenantUserSkills')) {
        await this.fetchTenantTeamTenantUserSkills();
      }
    },
    computed: {
      ...mapGetters('teamup', ['loaded']),
      ...mapState({
        tenantTeam: state => state.teamup.data.tenantTeam,
        tenantTeamPermissions: state => state.teamup.data.tenantTeamPermissions,
        tenantTeamTenantUsers: state => state.teamup.data.tenantTeamTenantUsers,
        tenantUser: state => state.tenantUser,
      }),
      isViewPage: function () {
        return this.$route.path.includes('view');
      },
      isSearchPage: function () {
        return this.$route.path.search('search');
      },
    },
    methods: {
      fetchTenantTeamPermissions: async function (tenantTeamCode) {
        try {
          const api = await new ApiTenantTeamPermission(tenantTeamCode);
          const res = await api.get();
          await this.$store.commit(
            `teamup/${this.isViewPage ? 'isViewOnly' : 'isNotViewOnly'}`);
          await this.$store.commit(
            'teamup/set', { key: 'tenantTeamPermissions', data: res.data });

          const permission = await res.data.filter(
            elem => elem.tenantUser.id === this.tenantUser.id);
          if (permission.length === 0 || permission[0].permissionType !== 100) {
            throw { response: { status: HttpStatus.FORBIDDEN } };
          }
        } catch (error) {
          if (error?.response?.status === HttpStatus.FORBIDDEN) {
            this.$store.commit('teamup/isViewOnly');
            if (this.$route.name !== 'teamup-view') {
              this.$router.push({
                name: 'teamup-view', params: { code: tenantTeamCode, }
              });
            }
          } else {
            this.handleFetchError(error);
          }
        }
      },
      fetchTenantTeamSkills: async function () {
        try {
          const api = await new ApiTenantTeamSkill(this.tenantTeam.id);
          const res = await api.get({ code: this.tenantTeam.code });
          await this.$store.commit(
              'teamup/set', { key: 'tenantTeamSkills', data: res.data });
        } catch (error) {
          this.handleFetchError(error);
        }
      },
      fetchTenantTeamTenantUsers: async function () {
        try {
          const api = await new ApiTenantTeamTenantUser(this.tenantTeam.id);
          const res = await api.get({ code: this.tenantTeam.code });
          await this.$store.commit(
              'teamup/set', { key: 'tenantTeamTenantUsers', data: res.data });
        } catch (error) {
          this.handleFetchError(error);
        }
      },
      fetchTenantTeamTenantUserSkills: async function () {
        try {
          const api = await new ApiTenantTeamTenantUserSkill(this.tenantTeam.id);
          const res = await api.get({ code: this.tenantTeam.code });
          await this.$store.commit(
              'teamup/set', { key: 'tenantTeamTenantUserSkills', data: res.data });
        } catch (error) {
          this.handleFetchError(error);
        }
      },
      fetchTenantTeamView: async function (tenantTeamCode) {
        try {
          const api = await new ApiTenantTeamView(tenantTeamCode);
          const res = await api.get();
          await this.$store.commit('teamup/set', { key: 'tenantTeam', data: res.data });
        } catch (error) {
          this.handleFetchError(error);
        }
      },
      handleFetchError: function (error) {
        this.$store.commit('teamup/initialize');
        this.$store.commit('notification/setWarning', 'Failed to fetch data');
        this.$router.push({ name: 'teamup-select' });
        console.error(error);
      },
    },
  };
</script>
